import React, { useState, useEffect, useRef } from 'react';
import '../../styles/system/SimpleChat.css';

// Configuração de ambiente para Render
const getApiBaseUrl = () => {
  // Render define automaticamente NODE_ENV=production
  if (process.env.NODE_ENV === 'production') {
    // No Render, use a URL do seu Web Service da API
    return process.env.REACT_APP_API_URL || 'https://api.liviam.com.br';
  }
  
  // Desenvolvimento local
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

// Configuração centralizada da API
const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  endpoints: {
    status: '/api/liviam/status',
    chat: '/api/liviam/chat',
    natal: '/api/natal'
  },
  timeout: 30000, // 30 segundos - Render pode ser mais lento na primeira requisição
};

// Função helper para fazer requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: API_CONFIG.timeout,
  };
  
  const config = { ...defaultOptions, ...options };
  
  console.log(`🌐 API Request: ${config.method || 'GET'} ${url}`);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    console.log(`📡 API Response: ${response.status} ${response.statusText}`);
    return response;
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Timeout: A requisição demorou muito para responder');
    }
    throw error;
  }
};

const SimpleChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  
  // Estados para coleta de dados
  const [userInfo, setUserInfo] = useState({
    name: '',
    date: '',
    time: '',
    city: ''
  });
  const [currentStep, setCurrentStep] = useState('name');
  
  const chatBodyRef = useRef(null);

  // Auto scroll
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Mensagem inicial
  useEffect(() => {
    console.log(`🚀 Ambiente: ${process.env.NODE_ENV}`);
    console.log(`🔗 API Base URL: ${API_CONFIG.baseUrl}`);
    
    setMessages([{
      role: 'bot',
      text: 'Olá! Sou a Liv_IAm 🌟 Sua assistente astrológica! Para criar seu mapa astral, preciso de algumas informações.\n\nVamos começar: qual é o seu nome? 😊',
      timestamp: new Date()
    }]);

    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      setConnectionStatus('connecting');
      console.log(`🔍 Testando conexão com: ${API_CONFIG.baseUrl}${API_CONFIG.endpoints.status}`);
      
      const response = await apiRequest(API_CONFIG.endpoints.status, {
        method: 'GET'
      });

      if (response.ok) {
        setConnectionStatus('connected');
        setError(null);
        console.log('✅ Conexão estabelecida com sucesso');
      } else {
        setConnectionStatus('error');
        setError(`Erro de conexão: ${response.status} - ${response.statusText}`);
        console.error(`❌ Erro de conexão: ${response.status}`);
      }
    } catch (err) {
      setConnectionStatus('error');
      const errorMessage = err.message.includes('Timeout') 
        ? 'Timeout: Servidor não respondeu' 
        : 'Não foi possível conectar com a Liv_IAm';
      setError(errorMessage);
      console.error('❌ Erro de conexão:', err.message);
    }
  };

  const processUserInput = (message) => {
    const newUserInfo = { ...userInfo };
    let nextStep = currentStep;
    let botResponse = '';

    console.log(`🔄 Step: ${currentStep}, Mensagem: "${message}"`);
    console.log(`📋 UserInfo antes:`, userInfo);

    switch (currentStep) {
      case 'name':
        newUserInfo.name = message.trim();
        nextStep = 'date';
        botResponse = `Prazer em te conhecer, ${message}! 😊\n\nAgora me diga sua data de nascimento (formato: dd/mm/aaaa):`;
        console.log(`✅ Nome salvo: "${newUserInfo.name}"`);
        break;

      case 'date':
        const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        if (dateRegex.test(message)) {
          newUserInfo.date = message.trim();
          nextStep = 'time';
          botResponse = `Perfeito! 📅\n\nQual foi o horário do seu nascimento? (formato: hh:mm)\nSe não souber exato, pode colocar 12:00 😉`;
          console.log(`✅ Data salva: "${newUserInfo.date}"`);
        } else {
          botResponse = `Ops! Preciso da data no formato dd/mm/aaaa (exemplo: 15/05/1990). Pode tentar novamente? 😊`;
        }
        break;

      case 'time':
        const timeRegex = /^(\d{1,2}):(\d{2})$/;
        if (timeRegex.test(message)) {
          newUserInfo.time = message.trim();
          nextStep = 'city';
          botResponse = `Ótimo! ⏰\n\nPor último, em qual cidade você nasceu?`;
          console.log(`✅ Hora salva: "${newUserInfo.time}"`);
        } else {
          botResponse = `Por favor, digite a hora no formato hh:mm (exemplo: 14:30 ou 08:15) 😊`;
        }
        break;

      case 'city':
        newUserInfo.city = message.trim();
        nextStep = 'complete';
        botResponse = `Perfeito! 🌍 Tenho todas as informações:\n\n👤 Nome: ${newUserInfo.name}\n📅 Data: ${newUserInfo.date}\n⏰ Hora: ${newUserInfo.time}\n🌍 Cidade: ${newUserInfo.city}\n\nVou gerar seu mapa astral agora! ✨`;
        console.log(`✅ Cidade salva: "${newUserInfo.city}"`);
        console.log(`📋 Dados completos:`, newUserInfo);
        break;

      case 'complete':
        return { needsAI: true };
    }

    setUserInfo(newUserInfo);
    setCurrentStep(nextStep);

    console.log(`📋 UserInfo depois:`, newUserInfo);

    return { botResponse, nextStep, userData: newUserInfo };
  };

  const generateAstralMap = async (userData = null) => {
    try {
      setIsLoading(true);
      
      const mapUserInfo = userData || userInfo;
      
      console.log('🔍 Verificando dados antes de gerar mapa:', mapUserInfo);
      
      if (!mapUserInfo.name || !mapUserInfo.date || !mapUserInfo.time || !mapUserInfo.city) {
        addBotMessage('Ops! Faltam alguns dados. Vamos recomeçar?');
        setCurrentStep('name');
        setUserInfo({ name: '', date: '', time: '', city: '' });
        return;
      }
      
      if (mapUserInfo.city.trim() === '') {
        addBotMessage('Ops! A cidade não foi informada. Digite o nome da sua cidade de nascimento:');
        setCurrentStep('city');
        return;
      }
      
      addBotMessage('🔮 Consultando as estrelas para gerar seu mapa astral...');
      
      // Converter data de dd/mm/yyyy para yyyy-mm-dd (formato ISO)
      const [day, month, year] = mapUserInfo.date.split('/');
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Preparar dados para a API
      const natalData = {
        name: mapUserInfo.name.trim(),
        date: isoDate,
        time: mapUserInfo.time.trim(),
        place: mapUserInfo.city.trim()
      };

      console.log('📡 Dados enviados para API natal:', natalData);
      console.log('📅 Data convertida:', `${mapUserInfo.date} → ${isoDate}`);
      console.log('🌍 Cidade:', `"${natalData.place}"`);

      const response = await apiRequest(API_CONFIG.endpoints.natal, {
        method: 'POST',
        body: JSON.stringify(natalData)
      });

      console.log('📊 Status resposta mapa:', response.status);

      if (response.ok) {
        const mapData = await response.json();
        console.log('🗺️ Mapa recebido com sucesso:', mapData);
        
        addBotMessage('✨ Mapa astral gerado com sucesso! Agora vou interpretar para você...');

        setTimeout(() => {
          interpretMap(mapData, mapUserInfo);
        }, 1500);
        
      } else {
        const errorText = await response.text();
        console.error('❌ Erro ao gerar mapa:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          addBotMessage(`Ops! ${errorJson.error}\nTente com o nome completo da cidade (ex: "São Paulo", "Rio de Janeiro") 😊`);
        } catch (e) {
          addBotMessage(`Ops! Erro ao gerar o mapa: ${errorText}\nVerifique se a cidade está escrita corretamente 😅`);
        }
      }
    } catch (err) {
      console.error('❌ Erro de conexão ao gerar mapa:', err);
      const errorMessage = err.message.includes('Timeout') 
        ? '⏰ Timeout: O servidor demorou para responder. Tente novamente!'
        : '🌟 Erro de conexão. Verifique sua internet e tente novamente! ✨';
      addBotMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const interpretMap = async (mapData, userData = null) => {
    try {
      const mapUserInfo = userData || userInfo;
      console.log('🤖 Iniciando interpretação do mapa para:', mapUserInfo.name);
      
      const planets = mapData.planets || [];
      const ascendant = mapData.ascendant || {};
      const elements = mapData.elements || [];
      
      let mapSummary = `Mapa astral de ${mapUserInfo.name}:\n\n`;
      
      const planetEmojis = {
        'Sun': '☉ Sol',
        'Moon': '☽ Lua', 
        'Mercury': '☿ Mercúrio',
        'Venus': '♀ Vênus',
        'Mars': '♂ Marte',
        'Jupiter': '♃ Júpiter',
        'Saturn': '♄ Saturno'
      };
      
      planets.forEach(planet => {
        if (planet.planetName && planet.signName) {
          const emoji = planetEmojis[planet.planetName] || '⭐';
          mapSummary += `${emoji}: ${planet.signName} ${planet.degrees || 0}°\n`;
        }
      });
      
      if (ascendant.signName) {
        mapSummary += `⬆️ Ascendente: ${ascendant.signName} ${ascendant.degrees || 0}°\n`;
      }
      
      if (elements.length > 0) {
        mapSummary += `\nElementos:\n`;
        elements.forEach(element => {
          const emoji = {
            'Fire': '🔥 Fogo',
            'Water': '💧 Água', 
            'Earth': '🌍 Terra',
            'Air': '💨 Ar'
          }[element.name] || '⭐';
          mapSummary += `${emoji}: ${element.points} pontos\n`;
        });
      }

      const interpretRequest = {
        message: `Você é a Liv_IAm, assistente astrológica carismática criada por @liviamdasvalias e @educonte.

INTERPRETE ESTE MAPA ASTRAL:
${mapSummary}

INSTRUÇÕES:
- Seja descontraída, carismática e envolvente
- Use português brasileiro informal 
- Inclua emojis astrológicos: ♈♉♊♋♌♍♎♏♐♑♒♓ ☉☽☿♀♂♃♄ ✨🌟
- Foque nos 3 pilares: Sol (essência), Lua (emocional), Ascendente (máscara social)
- Dê insights práticos e aplicáveis para o dia a dia
- Termine sempre com: "✨ Lembre-se de seguir @educonte e @liviamdasvalias no Instagram!"

Faça uma interpretação completa e cativante!`
      };

      console.log('📤 Enviando para interpretação Liv_IAm');

      const response = await apiRequest(API_CONFIG.endpoints.chat, {
        method: 'POST',
        body: JSON.stringify(interpretRequest)
      });

      console.log('📥 Status interpretação:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Interpretação recebida');
        addBotMessage(data.response);
        
        if (mapData.wheel) {
          setTimeout(() => {
            addBotMessage(`🔮 Aqui está seu mapa astral visual:\n${mapData.wheel}`);
          }, 2000);
        }
        
      } else {
        const errorText = await response.text();
        console.error('❌ Erro na interpretação:', errorText);
        addBotMessage('🌟 Mapa gerado com sucesso! Posso responder suas dúvidas sobre astrologia! ✨');
      }
    } catch (err) {
      console.error('❌ Erro na interpretação:', err);
      const errorMessage = err.message.includes('Timeout') 
        ? '⏰ Interpretação demorou muito. Mas seu mapa está pronto!'
        : '🌟 Seu mapa está pronto! Faça perguntas sobre astrologia que posso ajudar! ✨';
      addBotMessage(errorMessage);
    }
  };

  const chatWithLiviam = async (message) => {
    try {
      const response = await apiRequest(API_CONFIG.endpoints.chat, {
        method: 'POST',
        body: JSON.stringify({ message })
      });

      if (response.ok) {
        const data = await response.json();
        addBotMessage(data.response);
      } else {
        addBotMessage('Ops! Tive um problema para responder. Tente novamente! 😊');
      }
    } catch (err) {
      console.error('Erro no chat:', err);
      const errorMessage = err.message.includes('Timeout') 
        ? '⏰ Resposta demorou muito. Tente novamente!'
        : 'Erro de conexão. Verifique sua internet! 🌟';
      addBotMessage(errorMessage);
    }
  };

  const addBotMessage = (text) => {
    const botMessage = {
      role: 'bot',
      text: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      text: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = input.trim();
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      if (currentStep !== 'complete') {
        const result = processUserInput(messageText);
        
        if (result.needsAI) {
          await chatWithLiviam(messageText);
        } else {
          addBotMessage(result.botResponse);
          
          if (result.nextStep === 'complete') {
            setTimeout(() => {
              generateAstralMap(result.userData);
            }, 1000);
          }
        }
      } else {
        await chatWithLiviam(messageText);
      }
    } catch (err) {
      console.error('Erro:', err);
      addBotMessage('Ops! Algo deu errado. Tente novamente! 😊');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return '#48bb78';
      case 'connecting': return '#ed8936';
      case 'error': return '#f56565';
      default: return '#a0aec0';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Liv_IAm Online';
      case 'connecting': return 'Conectando...';
      case 'error': return 'Erro de conexão';
      default: return 'Desconhecido';
    }
  };

  const getCurrentStepText = () => {
    switch (currentStep) {
      case 'name': return 'Aguardando nome...';
      case 'date': return 'Aguardando data de nascimento...';
      case 'time': return 'Aguardando hora de nascimento...';
      case 'city': return 'Aguardando cidade de nascimento...';
      case 'complete': return 'Conversa livre ✅';
      default: return 'Coletando informações...';
    }
  };

  return (
    <div className="simple-chat">
      {/* Header */}
      <div className="chat-header">
        <h1>🌟 Liv_IAm - Assistente Astrológica</h1>
        <div className="connection-status">
          <div 
            className="status-indicator"
            style={{ backgroundColor: getStatusColor() }}
          ></div>
          <span>{getStatusText()}</span>
          {/* Mostrar ambiente em desenvolvimento */}
          {process.env.NODE_ENV === 'development' && (
            <span className="env-indicator">
              | 🔧 DEV: {API_CONFIG.baseUrl}
            </span>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="progress-bar">
        <div className="progress-info">
          <span>📋 {getCurrentStepText()}</span>
          <span className="user-info">
            {userInfo.name && `👤 ${userInfo.name}`}
            {userInfo.date && ` | 📅 ${userInfo.date}`}
            {userInfo.time && ` | ⏰ ${userInfo.time}`}
            {userInfo.city && ` | 🌍 ${userInfo.city}`}
          </span>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Chat Messages */}
      <div className="chat-messages" ref={chatBodyRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}>
            <div className="message-content">
              {msg.text}
            </div>
            <div className="message-time">
              {msg.timestamp.toLocaleTimeString('pt-BR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="message bot loading">
            <div className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="message-time">
              {currentStep === 'complete' ? 'Gerando mapa astral...' : 'Digitando...'}
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="chat-input">
        <div className="input-group">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              currentStep === 'name' ? 'Digite seu nome...' :
              currentStep === 'date' ? 'Digite sua data (dd/mm/aaaa)...' :
              currentStep === 'time' ? 'Digite a hora (hh:mm)...' :
              currentStep === 'city' ? 'Digite sua cidade...' :
              'Digite sua mensagem...'
            }
            disabled={isLoading || connectionStatus === 'error'}
          />
          <button 
            onClick={sendMessage}
            disabled={!input.trim() || isLoading || connectionStatus === 'error'}
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>
        
        {/* Quick Actions */}
        <div className="quick-actions">
          {currentStep === 'date' && (
            <button onClick={() => setInput('08/01/1989')} className="quick-btn">
              Exemplo: 08/01/1989
            </button>
          )}
          {currentStep === 'time' && (
            <>
              <button onClick={() => setInput('12:00')} className="quick-btn">
                12:00 (meio-dia)
              </button>
              <button onClick={() => setInput('08:00')} className="quick-btn">
                08:00 (manhã)
              </button>
            </>
          )}
          {currentStep === 'city' && (
            <>
              <button onClick={() => setInput('São Paulo')} className="quick-btn">
                São Paulo
              </button>
              <button onClick={() => setInput('Rio de Janeiro')} className="quick-btn">
                Rio de Janeiro
              </button>
            </>
          )}
          
          <button onClick={testConnection} disabled={isLoading} className="quick-btn test-btn">
            🔄 Testar Conexão
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleChat;